import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography, Alert, Button } from "@mui/material";
import { axiosService } from "shared/helpers/axios";
import { OAUTH_REDIRECT_URI } from "../config/oauth";
import MergeAccountDialog, { MERGE_DISMISSED_KEY } from "../forms/MergeAccountDialog";

/**
 * Handles the OAuth2 redirect callback.
 *
 * Two modes, distinguished by the `state` query-param:
 *   - Login:  state = "<provider>"           → POST /auth/social/<provider>/callback/
 *   - Link:   state = "<provider>:link"      → POST /auth/social/<provider>/link/
 *
 * After linking the user is redirected back to /settings/?section=linked-accounts.
 */
const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const calledRef = useRef(false);

  useEffect(() => {
    // Prevent double-fire in React 18 StrictMode
    if (calledRef.current) return;
    calledRef.current = true;

    const code = searchParams.get("code");
    const rawState = searchParams.get("state");

    if (!code || !rawState) {
      setError("Invalid callback — missing code or provider.");
      return;
    }

    const isLinkMode = rawState.endsWith(":link");
    const provider = isLinkMode ? rawState.slice(0, -5) : rawState;
    const endpoint = isLinkMode
      ? `/auth/social/${provider}/link/`
      : `/auth/social/${provider}/callback/`;

    axiosService
      .post(endpoint, { code, redirect_uri: OAUTH_REDIRECT_URI })
      .then((res) => {
        if (isLinkMode) {
          window.location.replace("/settings/?section=linked-accounts");
        } else {
          const isNewUser = res.data?.is_new_user === true;
          const dismissed = localStorage.getItem(MERGE_DISMISSED_KEY);
          if (res.data?.user?.id) {
            localStorage.setItem('otable_auth_uid', String(res.data.user.id));
          }
          if (isNewUser && !dismissed) {
            setShowMergeDialog(true);
          } else {
            // Hard redirect so the browser sends the newly-set auth cookies
            // on the first page load instead of hitting them in the same tick.
            window.location.replace("/");
          }
        }
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Authentication failed. Please try again.";
        setError(msg);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 2,
          px: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 420, borderRadius: 2 }}>
          {error}
        </Alert>
        <Button
          variant="text"
          onClick={() => navigate("/login/")}
          sx={{ color: "rgba(118, 75, 162, 0.85)", textTransform: "none" }}
        >
          Return to login
        </Button>
      </Box>
    );
  }

  return (
    <>
      <MergeAccountDialog
        open={showMergeDialog}
        onMerged={() => window.location.replace("/")}
        onDismiss={() => window.location.replace("/")}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: "rgba(118, 75, 162, 0.85)" }} />
        <Typography variant="body1" color="text.secondary">
          Completing sign in…
        </Typography>
      </Box>
    </>
  );
};

export default OAuthCallback;
